import {
  Provider,
  ProviderResultStatus,
  type ScoringSession,
} from '@asko/shared';
import type { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ResultCard } from './ResultCard';

/**
 * Ikkala provayder natijasi yonma-yon (BT §7.2, 4-ekran).
 *
 * BT §7.4: ikkalasi ham rad etsa — alohida xabar ko'rsatiladi.
 */

/** Qaysi provayder bilan hozir ish ketmoqda. */
export interface OtpBusy {
  provider: Provider;
  action: 'confirm' | 'resend';
}

interface ScoringResultsProps {
  session: ScoringSession;
  onChoose: (provider: Provider) => void;
  choosing: Provider | null;
  /** Provayder scoring o'rtasida so'ragan kod. */
  onConfirmOtp: (provider: Provider, code: string) => void;
  onResendOtp: (provider: Provider) => void;
  otpBusy: OtpBusy | null;
  otpError: { provider: Provider; error: ApiError } | null;
  /**
   * Scoringni qaytadan yuborish.
   *
   * Provayder texnik sababdan javob bermasligi mumkin (nosozlik,
   * vaqt tugashi). Bunda mijozni boshidan haydash — anketa ham, MyID
   * ham qaytadan — ortiqcha: ma'lumot o'zgarmagan, faqat so'rovni
   * takrorlash kerak.
   */
  onRetry: () => void;
  retrying: boolean;
}

const FAILED_STATUSES: ProviderResultStatus[] = [
  ProviderResultStatus.REJECTED,
  ProviderResultStatus.TIMEOUT,
];

export function ScoringResults({
  session,
  onChoose,
  choosing,
  onConfirmOtp,
  onResendOtp,
  otpBusy,
  otpError,
  onRetry,
  retrying,
}: ScoringResultsProps) {
  const { t } = useI18n();
  const { alif, uzum } = session.results;
  const allFailed = [alif, uzum].every((r) => FAILED_STATUSES.includes(r.status));
  const decided = session.chosenProvider !== null;
  // Texnik nosozlikni qaytarib ko'rish mumkin, rad javobini esa yo'q:
  // provayder «yo'q» degan bo'lsa, so'rovni takrorlash uni fikridan
  // qaytarmaydi va faqat vaqtni oladi.
  const canRetry =
    !decided && [alif, uzum].some((r) => r.status === ProviderResultStatus.TIMEOUT);

  const otpProps = (provider: Provider) => ({
    onConfirmOtp: (code: string) => onConfirmOtp(provider, code),
    onResendOtp: () => onResendOtp(provider),
    otpBusy: otpBusy?.provider === provider ? otpBusy.action : null,
    otpError: otpError?.provider === provider ? otpError.error : null,
  });

  return (
    <section>
      {allFailed ? (
        <div className="card empty-state">
          <div className="empty-state__icon" aria-hidden="true">
            ✕
          </div>
          <h2 className="empty-state__title">{t('results.allFailedTitle')}</h2>
          <p className="empty-state__hint">{t('results.allFailedHint')}</p>
          {canRetry && (
            <button
              className="btn btn--primary"
              type="button"
              onClick={onRetry}
              disabled={retrying}
            >
              {retrying ? t('results.retrying') : t('results.retry')}
            </button>
          )}
        </div>
      ) : (
        <p className="section-hint">
          {t('results.hint')} {t('results.recommendOnly')}
          {session.totalDurationMs !== null && (
            <span className="section-hint__timing">
              {' '}
              {t('results.timing', { seconds: (session.totalDurationMs / 1000).toFixed(1) })}
            </span>
          )}
        </p>
      )}

      <div className="results-grid">
        <ResultCard
          result={alif}
          onChoose={() => onChoose(Provider.ALIF)}
          choosing={choosing === Provider.ALIF}
          decided={decided}
          {...otpProps(Provider.ALIF)}
        />
        <ResultCard
          result={uzum}
          onChoose={() => onChoose(Provider.UZUM)}
          choosing={choosing === Provider.UZUM}
          decided={decided}
          {...otpProps(Provider.UZUM)}
        />
      </div>

      {/*
        Bitta provayder texnik sababdan yiqilsa ham tugma kerak: mijoz
        ikkinchisining taklifini olgan bo'lishi mumkin, lekin taqqoslash
        uchun ikkalasi ham kerak edi. Mijozni anketadan va MyID'dan
        qaytadan haydash shart emas — faqat so'rov takrorlanadi.
      */}
      {canRetry && !allFailed && (
        <div className="results-retry">
          <span className="results-retry__hint">{t('results.retryHint')}</span>
          <button className="btn" type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? t('results.retrying') : t('results.retry')}
          </button>
        </div>
      )}
    </section>
  );
}
